import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ProjectData } from '@/types/project';
import { formatCurrency, formatPercentage } from './format';
import { calculateMetrics } from './calculations';
import { PDF_COLORS, PDF_STYLES } from './pdf-styles';

export const exportToPDF = async (data: ProjectData): Promise<void> => {
  try {
    if (!data.details.projectName) {
      throw new Error('Project name is required for PDF export');
    }

    const doc = new jsPDF();
    const metrics = calculateMetrics(data);

    // Add Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...PDF_COLORS.primary);
    doc.text(`${data.details.projectName} - Cost Value Reconciliation`, 14, 20);

    // Add Timestamp
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...PDF_COLORS.muted);
    doc.text(`Generated on ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 30);

    // Add branding
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...PDF_COLORS.muted);
    doc.text('Generated by QS Tools', doc.internal.pageSize.width - 14, 20, { align: 'right' });

    // Project Details Section
    autoTable(doc, {
      startY: 40,
      head: [['Project Details', '']],
      body: [
        ['Project Name', data.details.projectName],
        ['Client Name', data.details.clientName || 'Not set'],
        [
          'Project Start',
          data.details.projectStartDate
            ? format(data.details.projectStartDate, 'dd/MM/yyyy')
            : 'Not set',
        ],
        [
          'Project End',
          data.details.projectEndDate
            ? format(data.details.projectEndDate, 'dd/MM/yyyy')
            : 'Not set',
        ],
        [
          'Reporting Period',
          data.details.reportingPeriodStart && data.details.reportingPeriodEnd
            ? `${format(data.details.reportingPeriodStart, 'dd/MM/yyyy')} to ${format(
                data.details.reportingPeriodEnd,
                'dd/MM/yyyy'
              )}`
            : 'Not set',
        ],
      ],
      ...PDF_STYLES.table,
      theme: 'grid',
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { cellWidth: 'auto' }
      },
      didParseCell: function(data) {
        if (data.section === 'body' && data.row.index % 2 === 1) {
          data.cell.styles.fillColor = [248, 248, 248]; // grey-100
        }
      }
    });

    // Summary Section
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [['Summary', '']],
      body: [
        ['Total Value', formatCurrency(metrics.totalRevenue)],
        ['Total Costs', formatCurrency(metrics.totalCosts)],
        ['Profit/Loss', formatCurrency(metrics.profit)],
        ['Margin', formatPercentage(metrics.margin)],
      ],
      ...PDF_STYLES.table,
      theme: 'grid',
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { cellWidth: 'auto' }
      },
      didParseCell: function(data) {
        if (data.section === 'body' && data.row.index % 2 === 1) {
          data.cell.styles.fillColor = [248, 248, 248]; // grey-100
        }
      }
    });

    // Value Section
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [['Value', '']],
      body: [
        ['Contract Value', formatCurrency(data.value.contractValue || 0)],
        ['Approved Variations', formatCurrency(data.value.approvedVariations || 0)],
        ['Unapproved Variations', formatCurrency(data.value.unapprovedVariations || 0)],
        ['Invoiced to Date', formatCurrency(data.value.invoicedToDate || 0)],
        ['Retention', formatPercentage(data.value.retention || 0)],
        ['Forecasted Value', formatCurrency(data.value.forecastedRevenue || 0)],
      ],
      ...PDF_STYLES.table,
      theme: 'grid',
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { cellWidth: 'auto' }
      },
      didParseCell: function(data) {
        if (data.section === 'body' && data.row.index % 2 === 1) {
          data.cell.styles.fillColor = [248, 248, 248]; // grey-100
        }
      }
    });

    // Costs Section
    const costRows = Object.entries(data.costs)
      .filter(([key]) => key !== 'budgetedCosts')
      .map(([key, value]) => [
        key.charAt(0).toUpperCase() + key.slice(1),
        formatCurrency(value.toDate || 0),
        formatCurrency(value.forecast || 0),
        formatCurrency((value.toDate || 0) + (value.forecast || 0)),
      ]);

    // Calculate totals
    const totals = Object.entries(data.costs)
      .filter(([key]) => key !== 'budgetedCosts')
      .reduce(
        (acc, [, value]) => ({
          toDate: acc.toDate + (value.toDate || 0),
          forecast: acc.forecast + (value.forecast || 0),
          total: acc.total + (value.toDate || 0) + (value.forecast || 0),
        }),
        { toDate: 0, forecast: 0, total: 0 }
      );

    // Add totals row
    costRows.push([
      'Total',
      formatCurrency(totals.toDate),
      formatCurrency(totals.forecast),
      formatCurrency(totals.total),
    ]);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [['Component', 'To Date', 'Forecast', 'Total']],
      body: costRows,
      ...PDF_STYLES.table,
      theme: 'grid',
      margin: { left: 14, right: 14 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50 },
        1: { cellWidth: 45, halign: 'right' },
        2: { cellWidth: 45, halign: 'right' },
        3: { cellWidth: 45, halign: 'right' }
      },
      // Style the totals row and alternating rows
      didParseCell: function(data) {
        const isLastRow = data.row.index === costRows.length - 1;
        if (isLastRow) {
          // Keep existing totals styling
          data.cell.styles.fillColor = PDF_COLORS.primary;
          data.cell.styles.textColor = PDF_COLORS.white;
          data.cell.styles.fontStyle = 'bold';
        } else if (data.section === 'body' && data.row.index % 2 === 1) {
          // Alternating rows (skip totals)
          data.cell.styles.fillColor = [248, 248, 248];
        }
      }
    });

    // Footer Section
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(...PDF_COLORS.muted);
      doc.text(
        `Generated on ${format(new Date(), 'dd/MM/yyyy HH:mm')}`,
        14,
        doc.internal.pageSize.height - 20
      );
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width - 20,
        doc.internal.pageSize.height - 20,
        { align: 'right' }
      );
    }

    // Save the PDF
    const filename = `${data.details.projectName.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_cvr_${format(
      new Date(),
      'yyyy-MM-dd'
    )}.pdf`;
    doc.save(filename);
  } catch (error) {
    console.error('PDF Export failed:', error);
    throw error instanceof Error
      ? error
      : new Error('Failed to export PDF report');
  }
};